import React, { useState } from 'react';
import { Box, Button, Typography, useTheme, IconButton, Tooltip, CircularProgress } from '@mui/material';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useGetTransactionCountStatusQuery, useExportTransactionStatsMutation } from '@/services/dashboardApi';
import excelIcon from '../../assets/excel-icon.svg';

interface TransactionChartProps {
  amcId?: string;
}

export const TransactionChart: React.FC<TransactionChartProps> = ({ amcId }) => {
  const [activePeriod, setActivePeriod] = useState<string>('1W');
  const theme = useTheme();
  const periods = [
    { label: '1W', value: '1W' },
    { label: '1M', value: '1M' },
    { label: '6M', value: '6M' },
    { label: '1Y', value: '1Y' },
    { label: 'All', value: 'All' },
  ];

  // Build API parameters with optional AMC filter
  const apiParams = React.useMemo(() => ({
    type: activePeriod as '1W' | '1M' | '6M' | '1Y' | 'All',
    amcId,
  }), [activePeriod, amcId]);

  const { data, isLoading } = useGetTransactionCountStatusQuery(apiParams);
  const [exportTransactionStats, { isLoading: isDownloading }] = useExportTransactionStatsMutation();

  const handleDownload = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      const blob = await exportTransactionStats(apiParams).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `transaction-count-status-${apiParams.type}.xlsx`;

      document.body.appendChild(link);

      // Use requestAnimationFrame to ensure proper download
      requestAnimationFrame(() => {
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      });
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const handlePeriodChange = (period: string) => {
    setActivePeriod(period);
  };

  const TimePeriodButton: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({
    label,
    active,
    onClick,
  }) => (
    <Button
      onClick={onClick}
      sx={{
        bgcolor: active ? theme.palette.primary.light : theme.palette.background.paper,
        color: active ? theme.palette.primary.main : theme.typography.h1.color,
        border: active ? theme.borders.borderMedium : theme.borders.borderTime,
        borderRadius: '20px',
        minWidth: '60px',
        fontWeight: theme.typography.subtitle1.fontWeight,
        '&:hover': {
          bgcolor: active ? theme.palette.primary.light : theme.palette.primary.light,
        },
      }}
    >
      {label}
    </Button>
  );

  // Separate optional summary row and time-series rows
  const summaryTotal =
    Array.isArray(data) && data.length > 0 ? (data[0] as any)?.totalTransactions : undefined;
  
  // Filter out any non timeseries item (like the summary row) and ensure validity
  const timeSeries = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    // Master logic: idx !== 0 is the time series, idx === 0 is the summary
    return data.filter((d: any, idx: number) => idx !== 0 && d && (d.TransactionDate || d.MonthName));
  }, [data]);

  const categories = timeSeries.map((item: any) => {
    if (activePeriod === '1Y' || activePeriod === '6M') {
      return item.MonthName || '';
    }
    const date = item.TransactionDate ? new Date(item.TransactionDate) : undefined;
    return date ? date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) : '';
  });

  const transactionChartOptions: Highcharts.Options = {
    credits: {
      enabled: false,
    },
    chart: {
      type: 'area',
    },
    title: {
      text: '', // Moved to custom header
    },
    xAxis: {
      categories: categories,
      lineColor: theme.palette.divider,
    },
    yAxis: {
      title: {
        text: '',
      },
      gridLineDashStyle: 'Dash',
      gridLineColor: theme.palette.divider,
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
          return this.value.toString();
        },
      },
    },
    legend: {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      itemStyle: {
        color: theme.typography.subtitle1.color,
        fontSize: theme.typography.subtitle1.fontSize?.toString(),
        fontWeight: theme.typography.subtitle1.fontWeight?.toString(),
        fontFamily: 'Roboto',
      },
      symbolRadius: 50,
      symbolHeight: 10,
      symbolWidth: 10,
      itemDistance: 30,
      itemMarginBottom: 5,
      labelFormatter: function (): string {
        const values: { [key: string]: string } = {
          'Total Transactions': (
            timeSeries.reduce(
              (acc: number, item: any) => acc + (Number(item.FailureCount) || 0) + (Number(item.PendingCount) || 0) + (Number(item.SuccessCount) || 0),
              0
            ) || 0
          ).toLocaleString('en-US'),
          'Successful Transactions': (
            timeSeries.reduce(
              (acc: number, item: any) => acc + (Number(item.SuccessCount) || 0),
              0
            ) || 0
          ).toLocaleString('en-US'),
          'Failed Transactions': (
            timeSeries.reduce(
              (acc: number, item: any) => acc + (Number(item.FailureCount) || 0),
              0
            ) || 0
          ).toLocaleString('en-US'),
          'Pending Transactions': (
            timeSeries.reduce(
              (acc: number, item: any) => acc + (Number(item.PendingCount) || 0),
              0
            ) || 0
          ).toLocaleString('en-US'),
        };
        return `${(this as any).name}<br/><span style="color:${theme.typography.h1.color}; font-size:${theme.typography.h3.fontSize?.toString()}">${values[(this as any).name] || ''}</span>`;
      },
    },
    plotOptions: {
      area: {
        fillOpacity: 0.1,
        marker: {
          radius: 4,
          lineWidth: 2,
        },
      },
      column: {
        borderRadius: 2,
      },
    },
    series: [
      {
        type: 'area',
        name: 'Total Transactions',
        data: timeSeries.map(
          (item: any) =>
            (Number(item.FailureCount) || 0) +
            (Number(item.PendingCount) || 0) +
            (Number(item.SuccessCount) || 0)
        ),
        color: theme.palette.areaChart.totalTransactions,
      },
      {
        type: 'area',
        name: 'Successful Transactions',
        data: timeSeries.map((item: any) => Number(item.SuccessCount) || 0),
        color: theme.palette.areaChart.successTranscations,
        marker: {
          symbol: 'circle',
        },
      },
      {
        type: 'column',
        name: 'Failed Transactions',
        data: timeSeries.map((item: any) => Number(item.FailureCount) || 0),
        color: theme.palette.areaChart.failedTranscations,
      },
      {
        type: 'column',
        name: 'Pending Transactions',
        data: timeSeries.map((item: any) => Number(item.PendingCount) || 0),
        color: (theme.palette as any).areaChart?.pendingTranscations || theme.palette.warning.main,
      },
    ],
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '10px',
        p: 3,
        flex: 1,
        minWidth: '300px',
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h3" sx={{ fontWeight: theme.typography.h3.fontWeight }}>
              Number of Transactions
            </Typography>
            <Tooltip title="Download Excel">
              <IconButton
                onClick={handleDownload}
                disabled={isDownloading || timeSeries.length === 0}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                {isDownloading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <img src={excelIcon} alt="Export" style={{ width: 20, height: 20 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            (+43%) than last week
          </Typography>
        </Box>
      </Box>

      {/* Chart */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Typography>Loading...</Typography>
        </Box>
      ) : (
        <HighchartsReact highcharts={Highcharts} options={transactionChartOptions} />
      )}

      {/* Time period buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
        {periods.map((period) => (
          <TimePeriodButton
            key={period.value}
            label={period.label}
            active={activePeriod === period.value}
            onClick={() => handlePeriodChange(period.value)}
          />
        ))}
      </Box>
    </Box>
  );
};
